import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Send, Sparkles, Lock, ShieldAlert, Loader2, RotateCcw, X } from "lucide-react";
import { addComment, listComments } from "../services/comments";
import { setLastRead } from "../services/reads";
import { translateComment, moderateComment } from "../services/gemini";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";

export const CaseComments = ({ caseId, caseItem, canParticipate, user, profile }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] === "es" ? "es" : "en";
  const designerId = caseItem?.selectedDesignerId || null;
  const [comments, setComments] = useState([]);
  const [pending, setPending] = useState([]); // optimistic messages: {tempId, status, ...}
  const [xtra, setXtra] = useState({}); // id -> {en, es, source} on-demand per-message translation
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fmtTime = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleString(lang === "es" ? "es-ES" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return "";
    }
  };

  const load = async () => {
    try {
      const list = await listComments(caseId, designerId);
      setComments(list);
      // Opening the case marks its chat as read for THIS user (lab/designer tracked apart).
      if (designerId) setLastRead(user.uid, caseId, Date.now());
    } catch {
      /* soft-fail */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canParticipate) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, canParticipate, designerId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments, pending]);

  // Ensure every message is translated individually (never reuse another message's text).
  useEffect(() => {
    comments.forEach((c) => {
      const hasBoth = c.en && c.es && c.en !== c.es;
      if (hasBoth || xtra[c.id]) return;
      translateComment(c.text)
        .then((res) => {
          if (res.status === "ok" && res.en && res.es && res.en !== res.es) {
            setXtra((m) => ({ ...m, [c.id]: { en: res.en, es: res.es, source: res.source } }));
          }
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  const patchPending = (tempId, patch) =>
    setPending((list) => list.map((p) => (p.tempId === tempId ? { ...p, ...patch } : p)));
  const removePending = (tempId) =>
    setPending((list) => list.filter((p) => p.tempId !== tempId));

  // Runs moderation -> translation -> save in the BACKGROUND. Security logic is unchanged:
  // the message is only persisted if moderation approves it.
  const processPending = async (item) => {
    patchPending(item.tempId, { status: "sending" });

    // 1) Platform protection (blocking contact info / off-platform attempts).
    try {
      const mod = await moderateComment(item.text);
      if (mod.status === "ok" && mod.blocked) {
        patchPending(item.tempId, { status: "blocked" });
        return;
      }
    } catch {
      /* moderation unavailable -> allow (do not block legitimate chat) */
    }

    // 2) Translation (best-effort). Shown under the bubble once available.
    let en = item.text;
    let es = item.text;
    let source = lang;
    try {
      const res = await translateComment(item.text);
      if (res.status === "ok") {
        en = res.en || item.text;
        es = res.es || item.text;
        source = res.source || lang;
      }
    } catch {
      /* translation unavailable -> keep original in both languages */
    }
    patchPending(item.tempId, { en, es, source });

    // 3) Persist. On success the optimistic bubble is replaced by the stored one.
    try {
      await addComment({
        caseId,
        designerId,
        authorId: item.authorId,
        authorName: item.authorName,
        authorRole: item.authorRole,
        text: item.text,
        source,
        en,
        es,
      });
      removePending(item.tempId);
      await load();
    } catch {
      patchPending(item.tempId, { status: "failed", en, es, source });
    }
  };

  const send = () => {
    const value = text.trim();
    if (!value) return;
    const item = {
      tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      text: value,
      authorId: user.uid,
      authorName: profile?.name || user.email,
      authorRole: profile?.role || "lab",
      _ts: Date.now(),
      status: "sending",
      en: null,
      es: null,
      source: lang,
    };
    setText(""); // clear input immediately (optimistic)
    setPending((list) => [...list, item]);
    processPending(item);
  };

  const retryPending = (tempId) => {
    const item = pending.find((p) => p.tempId === tempId);
    if (item) processPending(item);
  };

  if (!canParticipate) {
    return (
      <div className="rounded-lg border border-border bg-card p-6" data-testid="comments-locked">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" /> {t("comments.participantsOnly")}
        </div>
      </div>
    );
  }

  if (!designerId) {
    return (
      <div className="rounded-lg border border-border bg-card p-6" data-testid="comments-no-designer">
        <h3 className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
          <MessageSquare className="h-5 w-5" /> {t("comments.title")}
        </h3>
        <p className="text-sm text-muted-foreground">{t("comments.selectDesignerFirst")}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid="case-comments">
      <h3 className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
        <MessageSquare className="h-5 w-5" /> {t("comments.title")}
      </h3>
      <p className="mb-4 flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-muted-foreground" data-testid="comments-protection-notice">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" /> {t("comments.protectionNotice")}
      </p>

      <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
        {loading ? (
          <>
            <Skeleton className="h-16 w-3/4" />
            <Skeleton className="h-16 w-2/3" />
          </>
        ) : comments.length === 0 && pending.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="comments-empty">
            {t("comments.empty")}
          </p>
        ) : (
          [...comments.map((c) => ({ ...c, _key: c.id })), ...pending.map((p) => ({ ...p, _key: p.tempId, _pending: true }))]
            .sort((a, b) => (a._ts ?? 0) - (b._ts ?? 0))
            .map((c) => {
              const mine = c.authorId === user.uid;
              const tr = c._pending ? null : xtra[c.id];
              const enText = tr?.en || c.en || c.text;
              const esText = tr?.es || c.es || c.text;
              const src = tr?.source || c.source || lang;
              const primary = (lang === "es" ? esText : enText) || c.text;
              const secondary = lang === "es" ? enText : esText;
              const showSecondary = secondary && secondary !== primary;
              const isSending = c._pending && c.status === "sending";
              const isBlocked = c._pending && c.status === "blocked";
              const isFailed = c._pending && c.status === "failed";
              return (
                <div
                  key={c._key}
                  data-testid={c._pending ? `comment-pending-${c.status}` : `comment-${c.id}`}
                  className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg border px-3 py-2 ${
                      isBlocked || isFailed
                        ? "border-red-200 bg-red-50"
                        : mine
                        ? "border-brand/30 bg-brand/5"
                        : "border-border bg-secondary/50"
                    } ${isSending ? "opacity-70" : ""}`}
                  >
                    <div className="mb-0.5 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold">{c.authorName}</span>
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {c.authorRole === "designer" ? t("roles.designerShort") : t("roles.labShort")}
                      </span>
                      {!c._pending && c._ts && (
                        <span className="text-[10px] text-muted-foreground" data-testid={`comment-time-${c.id}`}>
                          {fmtTime(c._ts)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground">{primary}</p>

                    {isSending && (
                      <span className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="comment-status-sending">
                        <Loader2 className="h-3 w-3 animate-spin" /> {t("comments.sending")}
                      </span>
                    )}
                    {isBlocked && (
                      <div className="mt-1.5 border-t border-red-200 pt-1.5">
                        <p className="flex items-start gap-1 text-[11px] text-red-700">
                          <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" /> {t("comments.blockedHint")}
                        </p>
                        <div className="mt-1 flex gap-1.5">
                          <button type="button" onClick={() => retryPending(c.tempId)} className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-200" data-testid="comment-retry">
                            <RotateCcw className="h-3 w-3" /> {t("comments.retry")}
                          </button>
                          <button type="button" onClick={() => removePending(c.tempId)} className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-secondary" data-testid="comment-discard">
                            <X className="h-3 w-3" /> {t("comments.discard")}
                          </button>
                        </div>
                      </div>
                    )}
                    {isFailed && (
                      <div className="mt-1.5 border-t border-red-200 pt-1.5">
                        <p className="text-[11px] text-red-700">{t("comments.sendFailed")}</p>
                        <div className="mt-1 flex gap-1.5">
                          <button type="button" onClick={() => retryPending(c.tempId)} className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-200" data-testid="comment-retry">
                            <RotateCcw className="h-3 w-3" /> {t("comments.retry")}
                          </button>
                          <button type="button" onClick={() => removePending(c.tempId)} className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-secondary" data-testid="comment-discard">
                            <X className="h-3 w-3" /> {t("comments.discard")}
                          </button>
                        </div>
                      </div>
                    )}

                    {!isBlocked && !isFailed && showSecondary && (
                      <div className="mt-1.5 border-t border-border/60 pt-1.5">
                        <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-indigo-500">
                          <Sparkles className="h-3 w-3" /> {t("comments.aiTranslated")} · {src === lang ? t("comments.translation") : t("comments.original")}
                        </span>
                        <p className="text-xs italic text-muted-foreground">{secondary}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mt-4 flex items-end gap-2">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("comments.placeholder")}
          data-testid="comment-input"
          className="resize-none"
        />
        <Button
          onClick={send}
          disabled={!text.trim()}
          className="rounded-full bg-brand text-white hover:bg-brand/90"
          data-testid="comment-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
